// src/auth/AuthContext.jsx
import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState, saveAuth, saveTenant, saveTenants, clearAuthStorage, loadAuthStorage } from './useAuthState.js';
import { registerAuthCallbacks } from './apiFetch.js';
import { API_V1, getSessionHeaders } from '../services/api/config.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { state, dispatch } = useAuthState();
  const navigate = useNavigate();

  // Register global 401/403 callbacks so apiFetch can trigger navigation
  useEffect(() => {
    registerAuthCallbacks({
      onSessionExpired: () => {
        dispatch({ type: 'SESSION_EXPIRED' });
        navigate('/login', { replace: true });
      },
      onTenantDenied: () => {
        dispatch({ type: 'TENANT_DENIED' });
        navigate('/select-tenant', { replace: true });
      },
      onSuperadminTenantBlocked: () => {
        try { sessionStorage.setItem('sa_blocked_banner', '1'); } catch { /* private mode */ }
        navigate('/admin/users', { replace: true });
      },
      getIsSuperadmin: () => state.isSuperadmin,
      getAdminMode: () => state.adminMode,
    });
  }, [dispatch, navigate, state.isSuperadmin, state.adminMode]);

  const lastMeRefreshRef = useRef(0);

  const refreshMe = useCallback(async () => {
    if (!state.token) return;
    const now = Date.now();
    if (now - lastMeRefreshRef.current < 30_000) return;
    lastMeRefreshRef.current = now;

    let body;
    try {
      const res = await fetch(`${API_V1}/auth/me`, {
        headers: { Authorization: `Bearer ${state.token}`, ...getSessionHeaders() },
      });
      if (!res.ok) return;
      body = await res.json();
    } catch {
      return;
    }

    const next = !!body.is_superadmin;
    let navigated = false;

    // SA change → dispatch + nav handling
    if (next !== state.isSuperadmin) {
      dispatch({ type: 'SET_SUPERADMIN', value: next });
      if (state.isSuperadmin && !next) {
        // true → false: route out of admin mode
        dispatch({ type: 'SET_ADMIN_MODE', value: false });
        if ((state.tenants || []).length === 0) {
          navigate('/no-access', { replace: true });
        } else {
          navigate('/select-tenant', { replace: true });
        }
        navigated = true;
      } else if (!state.isSuperadmin && next) {
        // false → true: route into admin mode
        dispatch({ type: 'SET_ADMIN_MODE', value: true });
        navigate('/admin/users', { replace: true });
        navigated = true;
      }
    }

    // Tenant list / role drift → dispatch + (optional) nav handling
    const oldTenants = state.tenants || [];
    const newTenants = body.tenants || [];
    const tenantsDiverged = (
      newTenants.length !== oldTenants.length
      || newTenants.some(nt => {
        const ot = oldTenants.find(o => o.tenant_id === nt.tenant_id);
        return !ot || ot.role !== nt.role;
      })
    );
    if (tenantsDiverged) {
      saveTenants(newTenants);
      dispatch({ type: 'TENANTS_REFRESHED', tenants: newTenants });

      if (
        !navigated
        && state.activeTenant
        && !newTenants.find(t => t.tenant_id === state.activeTenant.tenant_id)
      ) {
        // Active tenant removed — bounce to picker (or no-access if SA didn't reroute already)
        localStorage.removeItem('auth_tenant_id');
        if (newTenants.length === 0 && !next) {
          navigate('/no-access', { replace: true });
        } else if (newTenants.length > 0) {
          navigate('/select-tenant', { replace: true });
        }
        // SAs with zero tenants are already handled by the SA-change branch above.
      }
    }
  }, [state.token, state.isSuperadmin, state.tenants, state.activeTenant, dispatch, navigate]);

  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState === 'visible') refreshMe();
    }
    window.addEventListener('focus', refreshMe);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refreshMe);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshMe]);

  // ── login ────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    dispatch({ type: 'CLEAR_ERROR' });
    let res;
    try {
      res = await fetch(`${API_V1}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getSessionHeaders(),
        },
        body: JSON.stringify({ email, password }),
      });
    } catch {
      dispatch({ type: 'SET_ERROR', error: 'Impossibile contattare il server. Controlla la connessione.' });
      return;
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const msg =
        body.error_code === 'AUTH_INVALID_CREDENTIALS' ? 'Email o password errati' :
        body.error_code === 'AUTH_USER_NOT_FOUND'      ? 'Account disattivato'     :
                                                         body.message || 'Errore di accesso';
      const ref = body.request_id ? ` (ref: ${body.request_id})` : '';
      dispatch({ type: 'SET_ERROR', error: `${msg}${ref}` });
      return;
    }

    const data = await res.json();
    const { access_token, tenants, is_superadmin } = data;

    if (!tenants || tenants.length === 0) {
      if (is_superadmin) {
        // Pure superadmin: zero user_tenants rows is the EXPECTED state per BE handoff.
        saveAuth({ token: access_token, tenants: [] });
        const userPayload = { email };
        try {
          const meRes = await fetch(`${API_V1}/auth/me`, {
            headers: { Authorization: `Bearer ${access_token}`, ...getSessionHeaders() },
          });
          if (meRes.ok) Object.assign(userPayload, await meRes.json());
        } catch { /* non-fatal */ }
        dispatch({ type: 'LOGIN_SUCCESS', phase: 'ready', user: userPayload, token: access_token, tenants: [] });
        dispatch({ type: 'SET_SUPERADMIN', value: true });
        dispatch({ type: 'SET_ADMIN_MODE', value: true });
        navigate('/admin/users', { replace: true });
        return;
      }
      saveAuth({ token: access_token, tenants: [] });
      dispatch({ type: 'LOGIN_SUCCESS', phase: 'no_tenants', user: null, token: access_token, tenants: [] });
      dispatch({ type: 'SET_SUPERADMIN', value: false });
      navigate('/no-access', { replace: true });
      return;
    }

    saveAuth({ token: access_token, tenants });

    // Fetch user info from /me (includes is_superadmin fresh from DB)
    let user = { email };
    try {
      const meRes = await fetch(`${API_V1}/auth/me`, { headers: { Authorization: `Bearer ${access_token}`, ...getSessionHeaders() } });
      if (meRes.ok) user = await meRes.json();
    } catch { /* non-fatal */ }

    const isSuperadmin = !!(user.is_superadmin ?? is_superadmin);

    if (tenants.length === 1) {
      // Auto-select single tenant
      saveTenant(tenants[0].tenant_id);
      let applications = [];
      try {
        const appRes = await fetch(`${API_V1}/user-config/tenants/${tenants[0].tenant_id}/applications`, {
          headers: { Authorization: `Bearer ${access_token}`, ...getSessionHeaders() },
        });
        if (appRes.ok) applications = await appRes.json();
      } catch { /* non-fatal */ }

      dispatch({ type: 'LOGIN_SUCCESS', phase: 'ready', user, token: access_token, tenants });
      dispatch({ type: 'SET_SUPERADMIN', value: isSuperadmin });
      dispatch({ type: 'TENANT_SELECTED', activeTenant: tenants[0], applications });
      navigate('/', { replace: true });
    } else {
      dispatch({ type: 'LOGIN_SUCCESS', phase: 'tenant_pick', user, token: access_token, tenants });
      dispatch({ type: 'SET_SUPERADMIN', value: isSuperadmin });
      navigate('/select-tenant', { replace: true });
    }
  }, [dispatch, navigate]);

  // ── selectTenant ─────────────────────────────────────────────────────
  const selectTenant = useCallback(async (tenantId) => {
    const tenant = state.tenants.find(t => t.tenant_id === tenantId);
    if (!tenant) return;
    saveTenant(tenantId);

    const token = loadAuthStorage().token;
    let applications = [];
    try {
      const res = await fetch(`${API_V1}/user-config/tenants/${tenantId}/applications`, {
        headers: { Authorization: `Bearer ${token}`, ...getSessionHeaders() },
      });
      if (res.ok) applications = await res.json();
    } catch { /* non-fatal */ }

    dispatch({ type: 'TENANT_SELECTED', activeTenant: tenant, applications });
    navigate('/', { replace: true });
  }, [state.tenants, dispatch, navigate]);

  // ── logout ────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    clearAuthStorage();
    dispatch({ type: 'LOGOUT' });
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);

  const setAdminMode = useCallback((value) => {
    dispatch({ type: 'SET_ADMIN_MODE', value });
  }, [dispatch]);

  const isTenantAdmin = state.activeTenant?.role === 'admin';

  const value = {
    ...state,
    isTenantAdmin,
    login,
    selectTenant,
    logout,
    setAdminMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
